import { useLeaveFeedback } from '@/api/hooks/useLeaveFeedback'
import { analytics } from '@/lib/analytics'
import { Button, ButtonText } from '@/ui/core/button'
import { Input } from '@/ui/core/input'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useEffect, useState } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'
import { NpsScale } from './NpsScale'

/**
 * The most a member can write. An answer to "what made you pick that" is a sentence or two, so
 * this is generous rather than a limit anyone will meet. It is here because nothing else capped
 * it: the column behind this is a TEXT, whose own ceiling is measured in bytes and answered with
 * a driver error rather than a sentence. The chat composer is capped for the same reason.
 */
const COMMENT_MAX = 1000

/**
 * Leaving feedback: the score and the reason, in one slide-up.
 *
 * It was a flow of its own — a screen for the score, a screen for the words, a screen to say
 * thanks — which is the shape a twenty-question clinical form wants and far more than two
 * questions want. A member who has decided to tell us something should be able to see the whole
 * of what is being asked and answer it without leaving the tab they were on. So both controls
 * sit here together, and the sheet is the whole interaction.
 *
 * Sending closes it and the toast does the thanking, which is what this app already does for
 * every other thing a member completes in a sheet: claiming the whitening kit, saving a card.
 * A failure cannot be told that way, though — a sheet is a native modal and draws in a window
 * above the whole React tree, so a toast raised while it is open appears behind it and the
 * member is never told. It is said in the sheet instead, under the button that failed, where it
 * also stays put rather than expiring after four seconds.
 */
export const FeedbackSheet = ({
	open,
	onClose,
}: { open: boolean; onClose: () => void }) => {
	const [score, setScore] = useState<number | null>(null)
	const [comment, setComment] = useState('')
	const [error, setError] = useState<string>()
	const { mutate: leaveFeedback, isPending } = useLeaveFeedback()
	const left = COMMENT_MAX - comment.length

	// Emptied on the way out, so the next opening is a fresh answer rather than the last one
	// still sitting there. Done on close rather than on open so nothing is cleared under the
	// slide as it leaves.
	useEffect(() => {
		if (!open) {
			setScore(null)
			setComment('')
			setError(undefined)
		}
	}, [open])

	const send = () => {
		if (score === null || isPending) {
			return
		}
		setError(undefined)
		analytics.trackEvent('left-feedback', { score })
		leaveFeedback(
			{ score, comment: comment.trim() || undefined },
			{
				onSuccess: () => {
					onClose()
					Toast.show({
						type: 'success',
						text1: 'Thanks for submitting your feedback!',
					})
				},
				onError: (failure) =>
					setError(failure.message || "We couldn't send that. Please try again."),
			}
		)
	}

	return (
		<Sheet open={open} onClose={onClose}>
			<View className="pt-2">
				<SheetTitle glyph="star">Leave Feedback</SheetTitle>

				{/* Set at the title's own inset rather than past it, because the scale wants the
				    width: eleven cells indented twice over come to twenty-five points each, which
				    is narrower than the two digits that have to sit in one of them. */}
				<VStack gap={24} className="px-4 pb-8">
					<VStack gap={12}>
						<Text size="body0" weight="bold">
							How likely are you to recommend Wally?
						</Text>
						<NpsScale value={score} onChange={setScore} />
					</VStack>

					<VStack gap={12}>
						{/* The count sits on the question's own line, held to the right. Under the
						    field it was a third thing stacked between the box and the button; up here
						    it reads as a note on the question, and the field keeps its own edge. */}
						<HStack className="items-baseline justify-between" gap={12}>
							<Text size="body0" weight="bold" className="flex-1">
								What made you pick that?
							</Text>
							{/* Written against its own ceiling, so the figure needs no words to say what
							    it is counting or how far it can go. There from the first keystroke
							    rather than appearing near the end: a count over its limit is a budget,
							    and a budget that turns up only once it is nearly spent is no use.
							    Red at the ceiling, where it stops being a count and becomes the reason
							    the next keystroke does nothing. */}
							<Text
								size="body2"
								tone={left === 0 ? undefined : 'soft'}
								className={left === 0 ? 'text-danger' : undefined}
								accessibilityLiveRegion="polite"
								// Read aloud, a pair of numerals either side of a slash is not a
								// sentence, so the words survive for anyone listening rather than
								// looking.
								accessibilityLabel={`${left} character${left === 1 ? '' : 's'} left`}
							>
								{comment.length}/{COMMENT_MAX}
							</Text>
						</HStack>
						<Input
							value={comment}
							onChangeText={setComment}
							placeholder="Type your answer"
							autoCapitalize="sentences"
							maxLength={COMMENT_MAX}
							multiline
							scrollEnabled
							// A field asking for a paragraph has to look like it wants one, and a
							// multiline field fills its box from the middle unless it is told to start
							// at the top.
							//
							// It is also held to a ceiling, and scrolls past it. Left to grow, a field
							// that accepts a thousand characters becomes a thousand characters tall and
							// carries the countdown and the Send button off the foot of the sheet — so
							// the longer someone wrote, the further they had to go to send it. The
							// composer in chat is bounded the same way.
							style={{
								minHeight: 104,
								maxHeight: 152,
								paddingTop: 14,
								paddingBottom: 14,
								textAlignVertical: 'top',
							}}
						/>
					</VStack>

					{/* Nothing to send until there is a score: the words are optional and the number
					    is the answer. */}
					<VStack gap={8}>
						<Button
							fullWidth
							size="lg"
							onPress={send}
							disabled={score === null || isPending}
							isLoading={isPending}
						>
							<ButtonText>Send</ButtonText>
						</Button>
						{!!error && (
							<Text size="body2" className="text-center text-danger">
								{error}
							</Text>
						)}
					</VStack>
				</VStack>
			</View>
		</Sheet>
	)
}
